import { Contact } from "components/_pages";
import Success from "components/_pages/Contact/Success";
import Subscribe from "components/_pages/Subscribe";
import SubscriptionSuccess from "components/_pages/Subscribe/SubscriptionSuccess";

import React from "react";

const Homepage = React.lazy(() => import(`components/_pages/Homepage`));
const AboutUs = React.lazy(() => import(`components/_pages/AboutUs`));
const BrandKeeper = React.lazy(() => import(`components/_pages/BrandKeeper`));
const CreditAssetsPlanner = React.lazy(() =>
  import("components/_pages/CreditAssetsPlanner")
);
const PrivacyPolicy = React.lazy(() =>
  import("components/_pages/Policies/PrivacyPolicy")
);
const CookiePolicy = React.lazy(() =>
  import("components/_pages/Policies/CookiePolicy")
);

// BrandKeeper Terms
const BrandKeeperTerms = React.lazy(() =>
  import("components/_pages/Terms/BrandKeeperTerms")
);

// 404 page
const NotFound = React.lazy(() => import("components/_pages/NotFound"));

// ServicePage Preview
const ServicePageTemplate = React.lazy(() =>
  import("components/_templates/ServicePage")
);

// Graphic Design & Copywriting
const GraphicDesignCopywriting = React.lazy(() =>
  import("components/_pages/GraphicDesignCopywriting")
);

// Brand Strategy & Identity
const BrandStrategyIdentity = React.lazy(() =>
  import("components/_pages/BrandStrategyIdentity")
);

// Marketing & Lead Generation
const MarketingLeadGeneration = React.lazy(() =>
  import("components/_pages/MarketingLeadGeneration")
);

// Web, App & System Development
const WebAppSystemDevelopment = React.lazy(() =>
  import("components/_pages/WebAppSystemDevelopment")
);

// Online Store Management
const OnlineStoreManagement = React.lazy(() =>
  import("components/_pages/OnlineStoreManagement")
);

// Special Projects
const SpecialProjects = React.lazy(() =>
  import("components/_pages/SpecialProjects")
);

export const meta = {
  home: [
    {
      name: "description",
      content:
        "Outgrown the DIY look? We help service businesses clarify their brand and elevate their presence with strategy, design, and digital solutions built for growth.",
    },
  ],
  about: [
    {
      name: "description",
      content:
        "Learn how Martley Strategy™ helps ambitious service businesses bridge the gap between world-class work and a brand that proves it.",
    },
  ],
  contactUs: [
    {
      name: "description",
      content:
        "Get in touch with Martley Strategy™. Discuss opportunities, explore custom packages, and identify strategies to grow your brand.",
    },
  ],
  subscribe: [
    {
      name: "description",
      content:
        "Subscribe to Martley Strategy™ for strategic branding insights, exclusive offers, and actionable growth resources.",
    },
  ],

  brandKeeper: [
    {
      name: "description",
      content:
        "Monthly brand support for service business owners done looking smaller than they are. Proposals, social, and every client touchpoint, handled.",
    },
  ],

  creditAssetsPlanner: [
    {
      name: "description",
      content:
        "Plan, estimate, and balance deliverable scopes against monthly credit allocations with the BrandKeeper™ Credit & Assets Planner.",
    },
  ],

  privacyPolicy: [
    { name: "description", content: "Read Martley Strategy™’s Privacy Policy." },
  ],
  cookiePolicy: [
    { name: "description", content: "Read Martley Strategy™’s Cookie Policy." },
  ],

  brandKeeperTerms: [
    { name: "description", content: "Read BrandKeeper™ Terms and Conditions." },
  ],

  graphicDesignCopywriting: [
    {
      name: "description",
      content:
        "Graphic design and copywriting that stays consistent, looks sharp, and helps your message convert across every touchpoint.",
    },
  ],

  brandStrategyIdentity: [
    {
      name: "description",
      content:
        "Brand strategy and identity services that define your positioning, clarify your message, and create a consistent foundation for long-term growth.",
    },
  ],

  marketingLeadGeneration: [
    {
      name: "description",
      content:
        "Marketing and lead generation systems that attract the right people, improve lead quality, and turn attention into measurable growth.",
    },
  ],

  webAppSystemDevelopment: [
    {
      name: "description",
      content:
        "Web, app, and system development that stays clear, stable, and easy to use, designed and built with structure, intention, and reliable QA.",
    },
  ],

  onlineStoreManagement: [
    {
      name: "description",
      content:
        "Online store management that keeps products accurate, inventory in sync, and orders moving smoothly without operational surprises.",
    },
  ],

  specialProjects: [
    {
      name: "description",
      content:
        "Special projects that turn early ideas, sketches, and custom concepts into clear visuals teams can review, align on, and move forward with confidence.",
    },
  ],
};

export const title = {
  home: "Martley Strategy™ | Build a Memorable Brand",
  about: "About Us | Martley Strategy™",
  contactUs: "Contact Us | Martley Strategy™",
  subscribe: "Sign Up | Martley Strategy™",

  brandKeeper: "BrandKeeper™ | Brand Support for Service Businesses",
  creditAssetsPlanner: "BrandKeeper™ Credit & Assets Planner | Martley Strategy™",

  privacyPolicy: "Privacy Policy | Martley Strategy™",
  cookiePolicy: "Cookie Policy | Martley Strategy™",

  brandKeeperTerms: "BrandKeeper™ Terms & Conditions | Martley Strategy™",

  graphicDesignCopywriting: "Graphic Design & Copywriting | Martley Strategy™",
  brandStrategyIdentity: "Brand Strategy & Identity | Martley Strategy™",
  marketingLeadGeneration: "Marketing & Lead Generation | Martley Strategy™",
  webAppSystemDevelopment: "Web, App & System Development | Martley Strategy™",
  onlineStoreManagement: "Online Store Management | Martley Strategy™",

  specialProjects: "Special Projects | Martley Strategy™",
};

// --- BrandKeeper social preview (og:*, twitter:*) ---
const brandKeeperOg = {
  title: title.brandKeeper,
  description: meta.brandKeeper[0].content,
  image: "/brandkeeper-og.jpg",
  type: "website",
};

// --- BrandKeeper structured data: Service, Offers, and FAQ ---
// A function because it needs the live origin to build absolute URLs.
// RouteMiddleware calls this automatically and injects it as JSON-LD.
// The FAQPage block is the highest-leverage piece for AEO: it is what
// Google's "People Also Ask" and AI answer engines (ChatGPT, Perplexity,
// AI Overviews) pull from directly when someone asks a question your
// FAQ already answers.
const brandKeeperSchema = (origin) => ({
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": `${origin}/` },
        { "@type": "ListItem", "position": 2, "name": "BrandKeeper", "item": `${origin}/brandkeeper` },
      ],
    },
    {
      "@type": "Service",
      "@id": `${origin}/brandkeeper#service`,
      "name": "BrandKeeper™",
      "serviceType": "Monthly brand support for service businesses",
      "areaServed": "Worldwide",
      "termsOfService": `${origin}/brandkeeper/terms`,
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "BrandKeeper™ Plans",
        "itemListElement": [
          {
            "@type": "Offer",
            "name": "Basic",
            "price": "1200",
            "priceCurrency": "USD",
            "url": `${origin}/brandkeeper`,
            "priceSpecification": {
              "@type": "UnitPriceSpecification",
              "price": "1200",
              "priceCurrency": "USD",
              "billingDuration": "P1M",
            },
            "description":
              "2 static designs monthly, 1 short video (under 10 seconds), 1 revision per asset, 2 consulting hours, 3 to 5 business day turnaround. USD 1,188 setup fee included in month one.",
          },
          {
            "@type": "Offer",
            "name": "Pro",
            "price": "2500",
            "priceCurrency": "USD",
            "url": `${origin}/brandkeeper`,
            "priceSpecification": {
              "@type": "UnitPriceSpecification",
              "price": "2500",
              "priceCurrency": "USD",
              "billingDuration": "P1M",
            },
            "description":
              "4 static designs monthly, 3 short videos (under 10 seconds), 2 revisions per asset, 1 month credit rollover, 6 consulting hours, 5 to 7 business day turnaround. USD 1,188 setup fee included in month one.",
          },
          {
            "@type": "Offer",
            "name": "Elite",
            "price": "3900",
            "priceCurrency": "USD",
            "url": `${origin}/brandkeeper`,
            "priceSpecification": {
              "@type": "UnitPriceSpecification",
              "price": "3900",
              "priceCurrency": "USD",
              "billingDuration": "P1M",
            },
            "description":
              "6 static designs monthly, 4 short videos (under 10 seconds), 3 revisions per asset, 2 month credit rollover, 12 consulting hours, 7 to 10 business day turnaround. USD 1,188 setup fee included in month one.",
          },
        ],
      },
    },
    {
      "@type": "FAQPage",
      "@id": `${origin}/brandkeeper#faq`,
      "mainEntity": [
        {
          "@type": "Question",
          "name": "How does the monthly plan work?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text":
              "Each month, a set of design assets is produced, reviewed, and delivered, ready to use across any platform. The brand system is managed throughout so every asset stays aligned.",
          },
        },
        {
          "@type": "Question",
          "name": "What if there are no existing brand assets?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text":
              "The setup covers that. A starter brand kit is built before monthly work begins, covering logo, fonts, and colors, so there is never a blank slate to work from.",
          },
        },
        {
          "@type": "Question",
          "name": "Can plans be changed later?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text":
              "Plans can move up or down at any time. Let Martley know ahead of the next billing cycle and the change is handled without interruption to the work.",
          },
        },
        {
          "@type": "Question",
          "name": "What happens if a month gets skipped?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text":
              "Pro clients roll up to 2 assets to the next month. Elite clients roll up to 4 assets across two months. Basic plans do not include rollovers.",
          },
        },
        {
          "@type": "Question",
          "name": "Who owns the brand files?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text":
              "Martley holds the master files securely throughout the engagement. Full file ownership is available as an add-on for active BrandKeeper™ clients.",
          },
        },
        {
          "@type": "Question",
          "name": "What is the Brand Foundation Build?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text":
              "It is the setup work done in month one before regular delivery begins. Covers a brand audit and cleanup, an internal Martley Strategy brand style guide, asset organization, a file handoff system, and an onboarding call. Flat at USD 1,188 across all plans, already included in the month one price. No separate invoice.",
          },
        },
        {
          "@type": "Question",
          "name": "What if there is no logo or brand identity yet?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text":
              "The Foundation Build assumes existing assets are in place to audit and organize. If the business needs a logo, full brand identity, or brand strategy built from scratch, those are handled as separate add-ons before BrandKeeper™ begins.",
          },
        },
        {
          "@type": "Question",
          "name": "Is there a long-term contract?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text":
              "Standard plans run month to month. For businesses looking for a longer commitment at a custom arrangement, long-term packages are available through direct conversation with the Martley team.",
          },
        },
      ],
    },
  ],
});

export const ROUTES_CONFIG = [
  {
    path: "/",
    exact: true,
    title: title.home,
    meta: meta.home,
    component: <Homepage />,
    canonical: "/",
  },

  /* Secondary homepage aliases prevention */
  {
    path: "/home",
    title: title.home,
    meta: meta.home,
    component: <Homepage />,
    canonical: "/",
    noindex: true,
  },
  {
    path: "/offers",
    title: title.home,
    meta: meta.home,
    component: <Homepage />,
    canonical: "/",
    noindex: true,
  },
  {
    path: "/feedbacks",
    title: title.home,
    meta: meta.home,
    component: <Homepage />,
    canonical: "/",
    noindex: true,
  },

  {
    path: "/about",
    title: title.about,
    meta: meta.about,
    component: <AboutUs />,
    canonical: "/about",
  },

  {
    path: "/contact-us",
    title: title.contactUs,
    meta: meta.contactUs,
    component: <Contact />,
    canonical: "/contact-us",
  },
  {
    path: "/message-sent",
    title: title.contactUs,
    meta: [],
    component: (
      <Contact>
        <Success />
      </Contact>
    ),
    canonical: "/message-sent",
    noindex: true,
  },

  {
    path: "/sign-up",
    title: title.subscribe,
    meta: meta.subscribe,
    component: <Subscribe />,
    canonical: "/sign-up",
  },
  {
    path: "/subscribed",
    title: title.subscribe,
    meta: [],
    component: (
      <Subscribe>
        <SubscriptionSuccess />
      </Subscribe>
    ),
    canonical: "/subscribed",
    noindex: true,
  },

  {
    path: "/brandkeeper",
    exact: true,
    title: title.brandKeeper,
    meta: meta.brandKeeper,
    component: <BrandKeeper />,
    canonical: "/brandkeeper",
    og: brandKeeperOg,
    schema: brandKeeperSchema,
  },
  {
    path: "/brandkeeper/pricing",
    exact: true,
    title: title.brandKeeper,
    meta: meta.brandKeeper,
    component: <BrandKeeper />,
    canonical: "/brandkeeper",
    og: brandKeeperOg,
    schema: brandKeeperSchema,
  },

  {
    path: "/brandkeeper/planner",
    exact: true,
    title: title.creditAssetsPlanner,
    meta: meta.creditAssetsPlanner,
    component: <CreditAssetsPlanner />,
    canonical: "/brandkeeper/planner",
  },

  {
    path: "/graphic-design-and-copywriting",
    exact: true,
    title: title.graphicDesignCopywriting,
    meta: meta.graphicDesignCopywriting,
    component: <GraphicDesignCopywriting />,
    canonical: "/graphic-design-and-copywriting",
  },

  {
    path: "/brand-strategy-and-identity",
    exact: true,
    title: title.brandStrategyIdentity,
    meta: meta.brandStrategyIdentity,
    component: <BrandStrategyIdentity />,
    canonical: "/brand-strategy-and-identity",
  },

  {
    path: "/marketing-and-lead-generation",
    exact: true,
    title: title.marketingLeadGeneration,
    meta: meta.marketingLeadGeneration,
    component: <MarketingLeadGeneration />,
    canonical: "/marketing-and-lead-generation",
  },

  {
    path: "/web-app-and-system-development",
    exact: true,
    title: title.webAppSystemDevelopment,
    meta: meta.webAppSystemDevelopment,
    component: <WebAppSystemDevelopment />,
    canonical: "/web-app-and-system-development",
  },

  {
    path: "/online-store-management",
    exact: true,
    title: title.onlineStoreManagement,
    meta: meta.onlineStoreManagement,
    component: <OnlineStoreManagement />,
    canonical: "/online-store-management",
  },

  {
    path: "/special-projects",
    exact: true,
    title: title.specialProjects,
    meta: meta.specialProjects,
    component: <SpecialProjects />,
    canonical: "/special-projects",
  },

  {
    path: "/privacy-policy",
    exact: true,
    title: title.privacyPolicy,
    meta: meta.privacyPolicy,
    component: <PrivacyPolicy />,
    canonical: "/privacy-policy",
  },
  {
    path: "/cookie-policy",
    exact: true,
    title: title.cookiePolicy,
    meta: meta.cookiePolicy,
    component: <CookiePolicy />,
    canonical: "/cookie-policy",
  },

  {
    path: "/brandkeeper/terms",
    exact: true,
    title: title.brandKeeperTerms,
    meta: meta.brandKeeperTerms,
    component: <BrandKeeperTerms />,
    canonical: "/brandkeeper/terms",
  },

  {
    path: "/service-template-preview",
    exact: true,
    title: "Service Template Preview | Martley Strategy™",
    meta: [],
    component: <ServicePageTemplate />,
    canonical: "/service-template-preview",
    noindex: true,
  },

  {
    title: "404 Page Not Found | Martley Strategy™",
    meta: [],
    component: <NotFound />,
    noindex: true,
    canonical: null,
  },
];